import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import * as _ from 'lodash-es';

import { environment } from '../../../../environments/environment';
import { GlobalService } from '../../../global/services/global.service';
import { JsonObjectFactoryService } from '../../../shared/_services/json-object-factory.service';

/**
 * Replicates the AngularJS `SystemMappingFactory` (system-mapping/SystemMappingService.js).
 *
 * - `loadJsonObject(actionCode, extra?)` — GET wrapper around `base_url/loadJsonObject`,
 *   used by both modals to populate dropdowns / lookups.
 *   Original calls: JsonObjectFactory.getJSONObj(GENERAL_CONFIG.base_url + '/loadJsonObject', { action_code, tax_year, jcd_key: 250, ... }).
 *   In Angular 12 we delegate to JsonObjectFactoryService.getJSONObj6 which:
 *     - merges GlobalService.globalParams + componentFilterParams + extras
 *     - issues an HTTP GET with all merged params as query string (matching legacy behavior)
 *
 * - `createSysAccount`, `editSysAccount`, `toggleClient` — POST endpoints. Each merges
 *   GlobalService.globalParams + filterParams with the hardcoded `jcd_key = 250`,
 *   matching the original `_.merge(GlobalService.globalParams, params, filterParams)` flow.
 *   We use plain HttpClient.post (form-encoded) because the original uses raw $http.post,
 *   not JsonObjectFactory.saveJSON (per ADMIN_TAB_MIGRATION_GUIDE §6.10 Pitfall M8).
 */
@Injectable({ providedIn: 'root' })
export class MasterDataService {
  private readonly baseUrl = environment.base_url;
  private readonly adminApiUrl = environment.admin_api_url;
  private readonly JCD_KEY = 250;

  constructor(
    private http: HttpClient,
    private globalService: GlobalService,
    private jsonObjectFactory: JsonObjectFactoryService,
  ) {}

  /**
   * GET /loadJsonObject?action_code=...&tax_year=...&jcd_key=250&<globalParams>&<extra>
   *
   * Mirrors the legacy `JsonObjectFactory.getJSONObj(..., { action_code, tax_year, jcd_key:250, ... })`.
   * `extra` is the per-call params (tax_year, scenario, sys_acct_key, screen_key, etc.).
   */
  loadJsonObject(actionCode: string, extra: Record<string, any> = {}): Observable<any> {
    const params: Record<string, any> = {
      action_code: actionCode,
      jcd_key: this.JCD_KEY,
      ...extra,
    };
    if (params.tax_year === undefined || params.tax_year === null || params.tax_year === '') {
      params.tax_year = this.currentTaxYear;
    }
    return this.jsonObjectFactory.getJSONObj6(
      `${this.baseUrl}/loadJsonObject`,
      params,
      this.globalService.global || {},
      {},
    );
  }

  /** POST /admin/createSysAcct — creates a new system account from the create modal. */
  createSysAccount(payload: any, filterParams: any = {}): Observable<any> {
    return this.postFormEncoded(
      `${this.adminApiUrl}/createSysAcct`,
      this.buildPayload(payload, filterParams),
    );
  }

  /** POST /gtw/editSysAcct — saves edits to a system account from the edit modal.
   * NB: Original AngularJS factory uses base_url, not admin_api_url, for this call. */
  editSysAccount(payload: any, filterParams: any = {}): Observable<any> {
    return this.postFormEncoded(
      `${this.baseUrl}/editSysAcct`,
      this.buildPayload(payload, filterParams),
    );
  }

  /** POST /admin/updateSysAcctActiveFlag — activates/deactivates clients for a sys account. */
  toggleClient(payload: any, filterParams: any = {}): Observable<any> {
    return this.postFormEncoded(
      `${this.adminApiUrl}/updateSysAcctActiveFlag`,
      this.buildPayload(payload, filterParams),
    );
  }

  // ------------------------------------------------------------------
  // Internals
  // ------------------------------------------------------------------

  private buildPayload(rawPayload: any, filterParams: any = {}): Record<string, any> {
    const globalParams = this.globalService.global?.globalParams || {};
    const merged: any = _.merge(
      {},
      globalParams,
      filterParams || {},
      { jcd_key: this.JCD_KEY, jsonObj: JSON.stringify(rawPayload || {}) },
    );
    return merged;
  }

  private postFormEncoded(url: string, params: Record<string, any>): Observable<any> {
    const body = new URLSearchParams();
    Object.keys(params || {}).forEach((key) => {
      const value = params[key];
      if (value !== undefined && value !== null) {
        body.set(key, typeof value === 'object' ? JSON.stringify(value) : String(value));
      }
    });
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
    return this.http.post<any>(url, body.toString(), { headers, withCredentials: true });
  }

  private get currentTaxYear(): any {
    const gp = this.globalService.global?.globalParams || {};
    return gp.tax_year ?? this.globalService.global?.inputs?.tax_year?.selected?.value ?? '';
  }
}
